import React from 'react';
import PropTypes from 'prop-types';

import { reduxForm, Field } from 'redux-form';
import { BOOKING_OVERTIME_FORM } from '../../app-main/components/reduxForms/constants/formNames';
import validateBookingOvertimeForm from './validate/validateBookingOvertimeForm';
import submitBookingOvertimeForm from './submit/submitBookingOvertimeForm';
import { showFormError } from '../../app-bookings-request/forms/bookingUtil';

import SelectField from '../../shared-components/reduxForms/fields/SelectField/SelectField';
import Button from '../../elements-buttons/Button';
import RequiredLabel from '../../elements-inputs/RequiredLabel';
import { TosCTAdisclaimer } from 'shared-components/terms/cta-disclaimer';
import currencySym from 'currency-symbol-map';
import { OvertimeSummary } from '../../elements-forms/overtime-summary';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

const BookingOvertimeForm = ({
  displayError,
  currencyType,
  handleSubmit,
  isProcessing,
  prepareOvertime,
  contractsQuote = {},
  hoursValue,
  multiplierValue,
  priceValue,
  hostID,
  intl
}) => {
  const hourOptions = [
    { value: 0.5,
      label: 0.5 },
    { value: 1,
      label: 1 },
    { value: 1.5,
      label: 1.5 },
    { value: 2,
      label: 2 },
    { value: 2.5,
      label: 2.5 },
    { value: 3,
      label: 3 },
    { value: 3.5,
      label: 3.5 },
    { value: 4,
      label: 4 },
    { value: 4.5,
      label: 4.5 }];

  const multiplierOptions = [
    { value: 'reduced',
      label: intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.hourly_rate', defaultMessage: 'Same as hourly rate ({price}/hr)' }, { price: intl.formatNumber(priceValue, { style: 'currency', currency: currencyType, minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
    },
    { value: 'default',
      label: intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.1_5_hourly_rate', defaultMessage: '1.5x hourly rate ({price}/hr)' }, { price: intl.formatNumber(priceValue * 1.5, { style: 'currency', currency: currencyType, minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
    }
  ];

  return (
    <form onSubmit={handleSubmit}>
        <div className="flex column">

          <hr className="full-width" />
          <RequiredLabel text={ intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.hours_overtime', defaultMessage: 'Hours of overtime' }) } />

          <Field
            component={SelectField}
            name="hours"
            className="form-fields"
            placeholder={ intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.select', defaultMessage: 'Select' }) }
            options={hourOptions}
            maxMenuHeight={260}
            attributes={{ 'data-testing-id': 'hours-field' }}
            onChange={prepareOvertime}
          />

          <hr className="full-width" />
          <RequiredLabel text={ intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.overtime_rate', defaultMessage: 'Overtime rate' }) } />

          <Field
            component={SelectField}
            name="multiplier"
            className="form-fields"
            placeholder={ intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.select', defaultMessage: 'Select' }) }
            options={multiplierOptions}
            maxMenuHeight={260}
            attributes={{ 'data-testing-id': 'multiplier-field' }}
            onChange={prepareOvertime}
          />

          <hr className="margin-sm-bottom full-width" />
          <div className="h5 margin-lg-top margin-md-bottom">
            <FormattedMessage
              id='inbox_bookingID_overtime.booking_overtime_form.estimated_payout'
              defaultMessage='Estimated overtime payout'
            />
          </div>

          <div className="overtime-summary margin-xl-bottom">
            <OvertimeSummary
              contractsQuote={contractsQuote}
              hoursValue={hoursValue}
              multiplierValue={multiplierValue}
              priceValue={priceValue}
              currencyType={currencyType}
              hostID={hostID}
            />
          </div>

          <div className="h6">
            <FormattedMessage
              id='inbox_bookingID_overtime.booking_overtime_form.what_next'
              defaultMessage='What’s next?'
            />
          </div>
          <p className="margin-xl-bottom">
            <FormattedMessage
              id='inbox_bookingID_overtime.booking_overtime_form.guest_48_hours'
              defaultMessage='Your guest will have 48 hours to review the charges. At that point, payment will be collected and deposited in your bank account.'
            />
          </p>

          <div className="help-block text-danger text-left">{ displayError }</div>

          <div className="cta-buttons flex">
            <Button
              type="submit"
              attributes={{ 'data-testing-id': 'booking-overtime-submit-btn' }}
              category="primary"
              isLoading={isProcessing}
              disabled={isProcessing}
            >
              <FormattedMessage
                id='inbox_bookingID_overtime.booking_overtime_form.submit_overtime'
                defaultMessage='Submit Overtime'
              />
            </Button>
          </div>

          <hr className="margin-sm-bottom full-width" />
          <TosCTAdisclaimer location="requestBkg" buttonLabel={ intl.formatMessage({ id: 'inbox_bookingID_overtime.booking_overtime_form.submit_overtime_label', defaultMessage: 'Submit Overtime' }) } />
      </div>
    </form>
  );
};

BookingOvertimeForm.propTypes = {
  currencyType: PropTypes.string,
  prepareOvertime: PropTypes.func,
  contractsQuote: PropTypes.object,
  hoursValue: PropTypes.number,
  multiplierValue: PropTypes.string,
  priceValue: PropTypes.number,
  hostID: PropTypes.string,
  booking: PropTypes.object,
  displayError: PropTypes.string,
  handleSubmit: PropTypes.func,
  isProcessing: PropTypes.bool,
  intl: intlShape
};

const config = {
  form: BOOKING_OVERTIME_FORM,
  onSubmit: submitBookingOvertimeForm,
  validate: validateBookingOvertimeForm,
  onSubmitFail: showFormError
};

export default injectIntl(reduxForm(config)(BookingOvertimeForm));
